// ============================================================================
//  CertificatesPanel.jsx -- compliance certificate lookup + actions.
//  Rendered as the "Compliance certificates" subtab on the Balances page.
// ============================================================================
//  - Cycle scope follows the page-header Prev/Current/Next (same controls
//    that drive statements). Search overrides across all history.
//  - View PDF / per-cert Send / cycle-wide Send all (warning popup).
//  Rare admin ops (backfill, recipient import, adopt-statement-primaries,
//  missing-recipients xlsx) live as API endpoints only -- removed from the
//  toolbar 2026-08-20 per Suresh. See CONTEXT.md for the curl/console
//  recipes if ever needed again.
// ============================================================================

import { useEffect, useState } from 'react';

async function jfetch(url, opts = {}) {
  const r = await fetch(url, { credentials: 'include', ...opts });
  if (!r.ok) throw new Error(r.status + ' ' + (await r.text().catch(() => '')));
  return r.json();
}

export default function CertificatesPanel({ canWrite = false, isAdmin = false, cycleKey = '', forceOpen = false }) {
  const [open, setOpen] = useState(forceOpen);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // Scope always follows the cycle selected in the page header (the same
  // Prev/Current/Next controls that drive statements). Typing a search
  // overrides -- searching a number/customer/other cycle works across
  // all history.
  async function load() {
    setBusy(true); setMsg(null);
    try {
      let url = '/api/compliance-certificates?limit=300';
      const t = q.trim();
      if (t) {
        if (/^\d{4}-\d{2}-[AB]$/i.test(t))      url += '&cycle_key=' + encodeURIComponent(t.toUpperCase());
        else if (/^(ECA-?)?[\d-]+$/i.test(t))   url += '&number=' + encodeURIComponent(t.replace(/^ECA-?/i, ''));
        else                                     url += '&customer=' + encodeURIComponent(t);
      } else if (cycleKey) {
        url += '&cycle_key=' + encodeURIComponent(cycleKey);
      }
      const j = await jfetch(url);
      setRows(j.rows || []);
    } catch (e) { setMsg('Load failed: ' + e.message); }
    finally { setBusy(false); }
  }
  // Reload when the panel opens or the header cycle changes (unless a
  // manual search is active).
  useEffect(() => {
    if (open && (rows === null || !q.trim())) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cycleKey]);

  async function sendCert(row) {
    if (!confirm('Email certificate ' + row.certificate_number + ' to ' + row.company_name
      + '’s certificate recipients?\n\n(Recipients are managed on the customer card in Reference.)')) return;
    setBusy(true); setMsg(null);
    try {
      const j = await jfetch('/api/compliance-certificates/send/' + row.id, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      setMsg((j.dryRun ? '[DRY RUN] ' : '') + 'Sent ' + row.certificate_number + ' to ' + (j.to || []).join(', ')
        + ((j.cc && j.cc.length) ? ' (cc ' + j.cc.join(', ') + ')' : ''));
      await load();
    } catch (e) { setMsg('Send failed: ' + e.message); }
    finally { setBusy(false); }
  }


  async function massSend() {
    // Only meaningful in cycle scope -- the button is hidden otherwise.
    const list = rows || [];
    if (list.length === 0) { setMsg('No certificates in this cycle to send.'); return; }
    const unsendable = list.filter(r => !r.sent_at).length;
    const ok = confirm(
      '⚠️ MASS SEND — please read\n\n'
      + 'This will EMAIL ' + list.length + ' certificate(s) for cycle ' + cycleKey
      + ' to each customer’s certificate recipients.\n\n'
      + (unsendable ? unsendable + ' of them have never been sent; the rest will be RE-SENT.\n\n' : '')
      + 'Customers with no certificate recipients will be skipped and reported.\n\n'
      + 'Send now?');
    if (!ok) return;
    setBusy(true); setMsg(null);
    try {
      // Chunked (2026-10-01): avoids gateway timeout + Graph rate-limit
      // bursts on large cycles.
      const allIds = list.map(r => r.id);
      let j = { sent: 0, skippedNoRecipients: 0, failed: 0, results: [] };
      for (let i = 0; i < allIds.length; i += 8) {
        const part = await jfetch('/api/compliance-certificates/send-bulk', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ certIds: allIds.slice(i, i + 8) })
        });
        j.sent += part.sent ?? 0;
        j.skippedNoRecipients += part.skippedNoRecipients ?? 0;
        j.failed += part.failed ?? 0;
        j.results = j.results.concat(part.results || []);
      }
      let m = 'Mass send for ' + cycleKey + ': ' + j.sent + ' sent';
      if (j.skippedNoRecipients) m += ', ' + j.skippedNoRecipients + ' skipped (no certificate recipients — add them on the customer card in Reference)';
      if (j.failed) m += ', ' + j.failed + ' FAILED — see console';
      setMsg(m);
      if (j.failed) console.warn('bulk send results', j.results);
      await load();
    } catch (e) { setMsg('Mass send failed: ' + e.message); }
    finally { setBusy(false); }
  }



  const fmtD = (v) => v ? String(v).slice(0, 10) : '';

  return (
    <div style={{ marginTop: forceOpen ? 0 : '1rem' }}>
      {!forceOpen && (
      <button className="refresh-btn" onClick={() => setOpen(o => !o)}
              style={{ background: open ? '#475569' : '#0f766e' }}>
        {open ? '▼ Hide compliance certificates' : '▶ Compliance certificates'}
      </button>
      )}
      {open && (
        <div className={forceOpen ? undefined : 'card'}
             style={forceOpen ? {} : { marginTop: '0.5rem', padding: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <input value={q} onChange={e => setQ(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && load()}
                   placeholder="Search: cert number, customer, or cycle (e.g. 2026-06-A)"
                   style={{ flex: 1, minWidth: 240, padding: '4px 8px' }} />
            <button className="refresh-btn" onClick={() => load()} disabled={busy}>Search</button>
            {canWrite && !q.trim() && (
              <button className="refresh-btn" onClick={massSend} disabled={busy || !(rows && rows.length)}
                      style={{ background: '#dc2626', fontWeight: 700 }}
                      title={'Email every certificate in cycle ' + (cycleKey || '') + ' to its recipients — warning popup first'}>
                📤 Send all ({cycleKey || '—'})
              </button>
            )}
          </div>
          {msg && <div className="small" style={{ padding: '0.4rem 0.6rem', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 4, marginBottom: '0.5rem', whiteSpace: 'pre-wrap' }}>{msg}</div>}
          {busy && rows === null && <div className="small" style={{ color: '#666' }}>Loading…</div>}
          {rows && rows.length === 0 && <div className="small" style={{ color: '#666' }}>No certificates found.</div>}
          {rows && rows.length > 0 && (
            <div style={forceOpen ? {} : { maxHeight: '26em', overflowY: 'auto' }}>
              <table className="datatable" style={{ margin: 0, fontSize: '0.87em' }}>
                <thead>
                  <tr>
                    <th>Certificate N°</th>
                    <th>Customer</th>
                    <th>Cycle</th>
                    <th className="num">Loads</th>
                    <th>Issued</th>
                    <th>Sent</th>
                    <th style={{ width: '11em' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td><code style={{ color: '#7f1d1d', fontWeight: 600 }}>{r.certificate_number}</code></td>
                      <td>{r.company_name || r.customer_canonical_name}</td>
                      <td>{r.cycle_key}</td>
                      <td className="num">{r.load_count}</td>
                      <td className="small">{fmtD(r.issued_at)}</td>
                      <td className="small">
                        {r.sent_at
                          ? <span title={(Array.isArray(r.sent_to) ? r.sent_to.join(', ') : '') + ' by ' + (r.sent_by || '')} style={{ color: '#15803d' }}>✓ {fmtD(r.sent_at)}</span>
                          : <span style={{ color: '#b45309' }}>not sent</span>}
                      </td>
                      <td>
                        <a href={'/api/compliance-certificates/pdf/' + r.id} target="_blank" rel="noreferrer"
                           className="refresh-btn small" style={{ background: '#0369a1', textDecoration: 'none', marginRight: 4 }}>
                          PDF
                        </a>
                        {canWrite && (
                          <button className="refresh-btn small" onClick={() => sendCert(r)} disabled={busy}
                                  style={{ background: r.sent_at ? '#94a3b8' : '#15803d' }}
                                  title={r.sent_at ? 'Already sent — resends to current recipients' : 'Email to certificate recipients'}>
                            {r.sent_at ? 'Resend' : 'Send'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
